import { ApolloError } from '@apollo/client/errors'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GqlResult } from 'uniswap/src/data/types'
import { CurrencyInfo } from 'uniswap/src/features/dataApi/types'
import { gqlTokenToCurrencyInfo, usePersistedError } from 'uniswap/src/features/dataApi/utils'
import {
  fetchGraphqlTopTokens,
  TopTokensGraphqlRequest,
} from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'

// Helper function to detect if search query is an address
function isAddress(value: string): boolean {
  return value.startsWith('0x')
}

export const TOP_TOKENS_OPERATION = 'SearchTokens'

// GraphQL 查询：按名称或符号搜索
export const SEARCH_TOKENS_BY_TEXT_QUERY = `query SearchTokens(
  $first: Int!
  $skip: Int!
  $searchQuery: String
) {
  tokens(
    first: $first
    skip: $skip
    orderBy: txCount
    orderDirection: desc
    where: {
      or: [
        { symbol_contains_nocase: $searchQuery }
        { name_contains_nocase: $searchQuery }
      ]
    }
  ) {
    id
    symbol
    name
    decimals
    txCount
    totalSupply
    __typename
  }
}`

// GraphQL 查询：按地址搜索
export const SEARCH_TOKENS_BY_ADDRESS_QUERY = `query SearchTokens(
  $first: Int!
  $skip: Int!
  $id: Bytes
) {
  tokens(
    first: $first
    skip: $skip
    orderBy: txCount
    orderDirection: desc
    where: {
      or: [
        { id: $id }
      ]
    }
  ) {
    id
    symbol
    name
    decimals
    txCount
    totalSupply
    __typename
  }
}`

export function useSearchTokens(
  searchQuery: string | null,
  skip: boolean,
  first: number = 100,
  skipCount: number = 0,
): GqlResult<CurrencyInfo[]> {
  const normalizedSearchQuery = (searchQuery ?? '').trim()
  const normalizedLowerCaseQuery = normalizedSearchQuery.toLowerCase()
  const isAddressSearch = isAddress(normalizedLowerCaseQuery)

  const [tokens, setTokens] = useState<any[] | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ApolloError | undefined>(undefined)
  const requestIdRef = useRef(0)

  const requestBody = useMemo(() => {
    // 根据是否是地址搜索选择不同的查询和变量
    let variables: any
    let query: string

    if (normalizedSearchQuery && isAddressSearch) {
      // 地址搜索
      variables = {
        id: normalizedSearchQuery,
        skip: skipCount,
        first,
      }
      query = SEARCH_TOKENS_BY_ADDRESS_QUERY
    } else {
      // 文本搜索（名称或符号）
      variables = {
        searchQuery: normalizedSearchQuery,
        skip: skipCount,
        first,
      }
      query = SEARCH_TOKENS_BY_TEXT_QUERY
    }

    const body: TopTokensGraphqlRequest = {
      variables,
      query,
      operationName: TOP_TOKENS_OPERATION,
    }

    return body
  }, [first, skipCount, normalizedSearchQuery, isAddressSearch])

  const executeRequest = useCallback(async () => {
    if (skip) {
      setLoading(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(undefined)

    try {
      const response = await fetchGraphqlTopTokens(requestBody)

      if (requestIdRef.current !== requestId) {
        return
      }

      setTokens(response?.data?.tokens ?? undefined)
    } catch (caughtError: unknown) {
      if (requestIdRef.current !== requestId) {
        return
      }

      const resolvedError =
        caughtError instanceof ApolloError
          ? caughtError
          : new ApolloError({
              errorMessage:
                caughtError instanceof Error ? caughtError.message : 'Failed to fetch tokens',
              networkError: caughtError instanceof Error ? caughtError : undefined,
            })

      setTokens(undefined)
      setError(resolvedError)
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [requestBody, skip])

  useEffect(() => {
    executeRequest()
  }, [executeRequest])

  const persistedError = usePersistedError(loading, error)

  const formattedData = useMemo(() => {
    if (!tokens) {
      return undefined
    }

    return tokens
      .map((token) => {
        if (!token) {
          return null
        }

        return gqlTokenToCurrencyInfo(token)
      })
      .filter((c): c is CurrencyInfo => Boolean(c))
  }, [tokens])

  const retry = useCallback(() => {
    if (skip) {
      return
    }

    return executeRequest()
  }, [executeRequest, skip])

  const result = useMemo(
    () => ({
      data: formattedData,
      loading,
      error: persistedError ?? error,
      refetch: retry,
    }),
    [formattedData, loading, persistedError, error, retry],
  )

  return result
}
